import 'reflect-metadata';
import 'mapbox-gl/dist/mapbox-gl.css';
import 'rsuite/dist/rsuite.min.css';
import 'styles/styles.css';
import 'styles/map.css';
import 'basscss/css/basscss.css';

import flagsmith from 'flagsmith/isomorphic';
import { FlagsmithProvider } from 'flagsmith/react';

import ThemeContextProvider from '../context/themes/index';
import Head from 'next/head';
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query';
import { SnackbarProvider, closeSnackbar } from 'notistack';
import SnackbarUtils, { SnackbarConfigurator } from '../utils/SnackbarUtils';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { datadogRum } from '@datadog/browser-rum';
import { appWithTranslation } from 'next-i18next';
import nextI18NextConfig from '../next-i18next.config';
import { registerTestIdDebugger } from '../utils/testIdDebugger';

function MyApp({ Component, pageProps, flagsmithState }) {
  datadogRum.init({
    applicationId: pageProps.datadog.applicationId, //'0cec75d8-3f39-467a-bc4a-90f6b0f18cb6',
    clientToken: pageProps.datadog.clientToken, //'pub955b0d1ad7d978fa1ed610722e9f0833',
    site: pageProps.datadog.site, //'us5.datadoghq.com',
    service: 'leadcast',
    env: pageProps.datadog.environment,
    // Specify a version number to identify the deployed version of your application in Datadog
    version: '1.0.0',
    sessionSampleRate: pageProps.datadog.sessionSampleRate ?? 100,
    sessionReplaySampleRate: pageProps.datadog.sessionReplaySampleRate ?? 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  });
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        cacheTime: 0,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query?.meta?.errorMessage) {
          SnackbarUtils.error(query.meta.errorMessage);
        }
        if (query?.meta?.warningMessage) {
          SnackbarUtils.warning(query.meta.errorMessage);
        }
      },
    }),
  });

  // Only register in development mode and client-side
  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    registerTestIdDebugger();
  }
  const getLayout = Component.getLayout || ((page) => page);

  return (
    <>
      <Head>
        <title>LeadCAST</title>
        <link rel="icon" type="x-icon" href="/leadcast/favicon.ico" />
      </Head>
      <FlagsmithProvider
        serverState={flagsmithState}
        options={{
          api: pageProps.flagsmith?.api,
          environmentID: pageProps.flagsmith?.environmentId,
          onError: (res) => console.error(res),
        }}
        flagsmith={flagsmith}
      >
        <QueryClientProvider client={queryClient}>
          <link
            href="https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;500;600;700;800;900&family=Open+Sans:wght@300;400;500;600;700;800&display=swap"
            rel="stylesheet"
            type="text/css"
          />
          <ThemeContextProvider>
            <SnackbarProvider
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              action={(snackbarId) => (
                <IconButton
                  title="Close"
                  size="small"
                  sx={{ marginInlineStart: '60px' }}
                  onClick={() => {
                    closeSnackbar(snackbarId);
                  }}
                >
                  <CloseIcon fontSize="inherit" sx={{ color: 'white' }} />
                </IconButton>
              )}
              autoHideDuration={3500}
            >
              <SnackbarConfigurator />
              {getLayout(<Component {...pageProps} />, pageProps)}
            </SnackbarProvider>
          </ThemeContextProvider>
        </QueryClientProvider>
      </FlagsmithProvider>
    </>
  );
}

MyApp.getInitialProps = async () => {
  const apiUrl = process.env.LEADCAST_API;
  const dataImporterUrl = process.env.LEADCAST_DATA_IMPORTER_API;
  const gisLayerURL = process.env.GIS_API;
  const tenantsApiUrl = process.env.TENANTS_API;
  const publicApiUrl = process.env.LEADCAST_PUBLIC_API;
  const usersApiUrl = process.env.LEADCAST_USERS_API;
  const flagsmithEnvironmentId = process.env.FLAGSMITH_ENVIRONMENT_ID;
  const flagsmithApiUrl = process.env.FLAGSMITH_API_URL;
  const arcGISAPIToken = process.env.ARCGIS_API_TOKEN;
  const datadogApplicationId = process.env.DATADOG_APP_ID;
  const datadogClientToken = process.env.DATADOG_CLIENT_TOKEN;
  const datadogSite = process.env.DATADOG_SITE;
  const datadogSessionSampleRate = process.env.DATADOG_SESSION_SAMPLE_RATE;
  const datadogSessionReplaySampleRate = process.env.DATADOG_REPLAY_SAMPLE_RATE;
  const datadogEnvironment = process.env.DATADOG_ENVIRONMENT ?? 'development';

  await flagsmith.init({
    // fetches flags on the server and passes them to the App
    environmentID: process.env.FLAGSMITH_ENVIRONMENT_ID,
    api: process.env.FLAGSMITH_API_URL,
  });

  return {
    pageProps: {
      apiUrl,
      dataImporterUrl,
      gisLayerURL,
      tenantsApiUrl,
      publicApiUrl,
      usersApiUrl,
      arcGISAPIToken,
      flagsmith: { environmentId: flagsmithEnvironmentId, api: flagsmithApiUrl },
      environment: process.env.NODE_ENV,
      datadog: {
        applicationId: datadogApplicationId,
        clientToken: datadogClientToken,
        site: datadogSite,
        sessionSampleRate: datadogSessionSampleRate
          ? Number.parseInt(datadogSessionSampleRate)
          : datadogEnvironment === 'development'
          ? 10
          : 100,
        sessionReplaySampleRate: datadogSessionReplaySampleRate
          ? Number.parseInt(datadogSessionReplaySampleRate)
          : 100,
        environment: datadogEnvironment,
      },
    },
    flagsmithState: flagsmith.getState(),
  };
};

export default appWithTranslation(MyApp, nextI18NextConfig);
